import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useSeason } from "@/lib/SeasonContext";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Trophy, ChevronRight, MapPin, Clock, LogIn } from "lucide-react";

const LOGO_URL = "/nphs-logo.png";
const HERO = "https://images.pexels.com/photos/5549509/pexels-photo-5549509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T19:00:00");
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return null;
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return `${diff} days`;
}

export default function Home() {
  const [data, setData] = useState(null);
  const { activeSeason } = useSeason();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, [activeSeason]);

  if (!data) return <div className="p-10 text-ink-300">Loading…</div>;

  const { record, team_ppg, next_game, recent_games, top_scorers } = data;
  const totalGames = record.games_played;

  return (
    <div data-testid="public-home">
      {/* Hero */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-ink-950 via-ink-950/85 to-burgundy-dark/40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(107,15,26,0.35),transparent_55%)]" />

        {!user && (
          <Link
            to="/login"
            className="absolute top-4 right-4 md:top-6 md:right-8 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ink-900/80 backdrop-blur border border-burgundy/50 text-white text-xs uppercase tracking-wider font-bold hover:bg-burgundy hover:border-burgundy transition"
            data-testid="hero-coach-login-link"
          >
            <LogIn className="w-3.5 h-3.5" /> Coach Login
          </Link>
        )}

        <div className="relative w-full px-6 md:px-12 py-12 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 animate-fade-up">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="" className="w-14 h-14 rounded-full ring-2 ring-burgundy bg-black" />
              <div>
                <p className="eyebrow text-[11px]">North Plainfield High School Boys Basketball</p>
                <p className="text-ink-300 text-xs uppercase tracking-[0.18em] mt-1">Coach Derrick Canada · Head Coach</p>
              </div>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tight">
              NPHS <span className="text-burgundy">Canucks</span>
              <br />Boys Basketball
            </h1>
            <p className="text-ink-300 mt-6 max-w-xl text-base md:text-lg leading-relaxed">
              The official live stats and game tracker for varsity Canucks basketball.
              Every shot, every win, every record — all in one place.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <div className="bg-ink-900/80 backdrop-blur border border-burgundy/40 rounded-md px-5 py-3">
                <p className="eyebrow text-[10px] text-ink-300">Season {activeSeason || ""}</p>
                <p className="font-display text-3xl font-black mt-1" data-testid="hero-record">
                  {record.wins}<span className="text-ink-500">-</span>{record.losses}
                  {totalGames > 0 && <span className="text-base text-ink-400 ml-2 font-bold">· {team_ppg} PPG</span>}
                </p>
              </div>
              <div className="flex gap-2">
                <Link to="/schedule" className="inline-flex items-center gap-2 bg-burgundy hover:bg-burgundy-hover px-5 py-3 rounded-md text-sm font-bold uppercase tracking-wider transition" data-testid="hero-schedule-cta">
                  <Calendar className="w-4 h-4" /> Full Schedule
                </Link>
                <Link to="/leaders" className="inline-flex items-center gap-2 border border-ink-700 hover:bg-ink-800 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-wider transition" data-testid="hero-leaders-cta">
                  <Trophy className="w-4 h-4" /> All-Time
                </Link>
              </div>
            </div>
          </div>

          {/* Next game card */}
          <div className="lg:col-span-2 animate-fade-up">
            <div className="bg-ink-900/95 backdrop-blur border border-burgundy/40 rounded-lg p-6 md:p-8 shadow-2xl">
              <p className="eyebrow flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Next Up</p>
              {next_game ? (
                <>
                  <p className="font-display text-4xl md:text-5xl font-black uppercase mt-3 leading-none">
                    {next_game.home_away === "home" ? "vs" : "@"} <br />
                    <span className="text-burgundy">{next_game.opponent}</span>
                  </p>
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="flex items-center gap-3 text-ink-300">
                      <Calendar className="w-4 h-4 text-burgundy" />
                      <span className="text-white">{next_game.date}</span>
                      {daysUntil(next_game.date) && <span className="ml-auto text-burgundy font-bold uppercase tracking-wider text-xs">{daysUntil(next_game.date)}</span>}
                    </div>
                    {next_game.location && (
                      <div className="flex items-center gap-3 text-ink-300">
                        <MapPin className="w-4 h-4 text-burgundy" />
                        <span className="text-white">{next_game.location}</span>
                      </div>
                    )}
                    {next_game.playoff && (
                      <p className="inline-block px-3 py-1 bg-amber-500/20 text-amber-400 text-xs uppercase tracking-wider rounded font-bold">Playoff Game</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="mt-3 text-ink-300">No upcoming games scheduled.</p>
              )}
              <Link to="/schedule" className="block mt-6 text-xs uppercase tracking-wider text-burgundy hover:text-burgundy-hover font-bold">
                See full schedule →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Top Scorers strip */}
      {top_scorers.length > 0 && (
        <section className="px-6 md:px-12 py-12 border-t border-ink-700/40">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="eyebrow">Leading the Charge</p>
              <h2 className="heading-2">Top Scorers</h2>
            </div>
            <Link to="/leaders" className="hidden md:inline-flex items-center gap-1 text-xs uppercase tracking-wider text-burgundy hover:text-burgundy-hover font-bold">
              All-Time Records <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {top_scorers.map((tp, i) => (
              <Link
                key={tp.player.id}
                to={`/player/${tp.player.slug}`}
                className="relative bg-ink-800 border border-white/5 rounded-lg p-6 hover:border-burgundy/50 hover:-translate-y-0.5 transition-all"
                data-testid={`home-top-scorer-${tp.player.slug}`}
              >
                <span className="absolute top-4 right-4 font-display text-5xl font-black text-burgundy/30">#{i + 1}</span>
                <PlayerAvatar player={tp.player} size="lg" />
                <p className="font-display text-2xl font-bold uppercase mt-4 leading-tight">{tp.player.player_name}</p>
                <p className="text-ink-400 text-xs">#{tp.player.jersey_number || "—"} · {tp.player.position || "—"}</p>
                <div className="flex gap-6 mt-4">
                  <div><p className="eyebrow text-[9px]">PPG</p><p className="font-display text-2xl font-black">{tp.ppg}</p></div>
                  <div><p className="eyebrow text-[9px]">REB</p><p className="font-display text-2xl font-black">{tp.reb_avg}</p></div>
                  <div><p className="eyebrow text-[9px]">AST</p><p className="font-display text-2xl font-black">{tp.ast_avg}</p></div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recent results */}
      {recent_games.length > 0 && (
        <section className="px-6 md:px-12 py-12 border-t border-ink-700/40">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="eyebrow">Latest Action</p>
              <h2 className="heading-2">Recent Results</h2>
            </div>
            <Link to="/schedule" className="hidden md:inline-flex items-center gap-1 text-xs uppercase tracking-wider text-burgundy hover:text-burgundy-hover font-bold">
              Full Schedule <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recent_games.map((g) => {
              const result = g.nphs_score > g.opp_score ? "W" : "L";
              return (
                <Link
                  key={g.id}
                  to={`/game/${g.id}`}
                  className="bg-ink-800 border border-white/5 rounded-lg p-6 hover:border-burgundy/50 hover:-translate-y-0.5 transition-all"
                  data-testid={`home-recent-${g.id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="eyebrow text-[10px]">{g.date}</p>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-bold ${result === "W" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{result === "W" ? "Win" : "Loss"}</span>
                  </div>
                  <p className="font-display text-2xl font-black uppercase mt-3 leading-tight">
                    {g.home_away === "home" ? "vs" : "@"} {g.opponent}
                  </p>
                  <p className="font-display text-4xl font-black mt-3">
                    <span className={result === "W" ? "text-green-400" : "text-red-400"}>{g.nphs_score}</span>
                    <span className="text-ink-500"> - </span>
                    <span className="text-ink-300">{g.opp_score}</span>
                  </p>
                  <p className="text-xs uppercase tracking-wider text-burgundy mt-3 font-bold">View Box Score →</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Quick links */}
      <section className="px-6 md:px-12 py-12 border-t border-ink-700/40 mb-6">
        <h2 className="heading-2 mb-6">Explore</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickLink to="/schedule" icon={Calendar} title="Schedule" desc="Every game, color-coded calendar + box scores" />
          <QuickLink to="/search" icon={Users} title="Players" desc="Search the active roster or alumni" />
          <QuickLink to="/leaders" icon={Trophy} title="All-Time Leaders" desc="School records across every season" />
        </div>
      </section>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 bg-ink-800 border border-white/5 rounded-lg p-6 hover:border-burgundy/50 hover:bg-ink-800/60 transition"
      data-testid={`home-quicklink-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-12 h-12 rounded-md bg-burgundy/15 border border-burgundy/40 flex items-center justify-center text-burgundy group-hover:bg-burgundy group-hover:text-white transition">
        <Icon className="w-5 h-5" strokeWidth={2.4} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl font-bold uppercase leading-none">{title}</p>
        <p className="text-ink-400 text-xs mt-1">{desc}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-ink-500 group-hover:text-burgundy group-hover:translate-x-0.5 transition" />
    </Link>
  );
}
